import { z } from 'zod';

/**
 * 组织架构的领域契约：部门与岗位。
 *
 * 这一份对着 `department` / `position` 两张**领域表**，不对着 `Node` 树。
 * 两者的区别不是实现选型，是 I-1：
 *
 * - `Node` 是一棵**类型可配**的通用树——客户能建「工段」「班组」，也能把 ATC 挂在
 *   企业下面。它把「层的身份」交给了客户去配，而 I-1 恰恰约束的就是层的身份
 *   （ADR-0020 二）。
 * - `department` / `position` 是**三层固定**的领域表：企业 → 部门 → 岗位，
 *   ATC 只挂岗位。这一条由库级 NOT NULL 外键硬保，不靠界面自觉。
 *
 * 所以写路径落在这一份上，不落在 `Node` 上——见 ADR-0020 六（形态 1）与 HUB-ARC-001。
 *
 * **部门只取一级职能部门**（ADR-0048、C-076 一）：`department.parent_department_id`
 * 这一列在库里存在（ADR-0020 允许部门自嵌套），但本契约**不暴露它**，
 * 建部门时永远写 null。子部门一律拆配到岗位。
 * 不暴露不是删除——判据变了还能再开，删了列就要写迁移。
 */

/* ═══════ 基础字段 ═══════ */

/** 部门与岗位的编码：客户自己维护，同租户内同类不重复。 */
export const orgCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_-]{0,31}$/, '1–32 位大写英文、数字、下划线或连字符');

export const orgNameSchema = z.string().trim().min(1, '请填写名称').max(60);

/**
 * 岗位状态。`draft` 是**默认落点**——C-05 四项没填齐的岗位存得下，但进不了 `active`，
 * 由库级约束 `position_active_requires_triplet` 拒绝。
 *
 * 这条很要紧：**不必把四项做成必填表单**，可以先建后补。
 * 把关的是库，不是界面——界面把关会被下一个界面绕过去。
 */
export const positionStatusSchema = z.enum(['draft', 'active', 'suspended', 'retired']);
export type PositionStatus = z.infer<typeof positionStatusSchema>;

export const departmentStatusSchema = z.enum(['active', 'merged', 'retired']);
export type DepartmentStatus = z.infer<typeof departmentStatusSchema>;

/**
 * ATC 状态**七态**（库级 `atc_status_check` 原文的顺序）。
 *
 * `proposed` 是 ADR-0021 六新增的**入池待建态**——它先于 `draft`：
 * 「这件事该有一张卡」已经成立，但卡本身还没开始写。
 * 少算它一个，台账的状态列就会在遇到待建卡时落到一个没有译名的分支上。
 */
export const atcStatusSchema = z.enum([
  'proposed',
  'draft',
  'in_review',
  'certified',
  'active',
  'suspended',
  'retired',
]);
export type AtcStatus = z.infer<typeof atcStatusSchema>;

/* ═══════ 三张资产表的状态中文译名（C-169 A5 · 裁定五） ═══════
 *
 * **三张表分开列，不硬凑成一张表。** 取值本来就不一样：ATC 七个、岗位四个、
 * 部门三个（部门没有 `suspended`）。凑成一个 map 的代价是界面上会出现
 * 「部门也能停用」这种库会拒的状态——而它不报错，只是显示错。
 *
 * **原则：同一个英文值在三张表里必须是同一个中文。** 否则就是一处一名两指，
 * 读表的人无从知道两张表上的「在役」是不是同一件事。
 *
 * ## `draft` 是唯一允许的不一致，且它是**有意**的
 *
 * | 表 | `draft` 念作 | 因为它指的是 |
 * |---|---|---|
 * | atc | 待补全 | **四段与 TEST** 没写完（第四张统计卡数的就是它） |
 * | position | 待上线 | **四项**（责任 / 权限边界 / 胜任标准 / 岗位空间）没齐 |
 *
 * 两者不是同一件事，用同一个中文反而是错的。这一处不一致在术语页写明——
 * 不写明，它看起来就只是个笔误。
 */

/** ATC 七态的中文。 */
export const ATC_STATUS_LABEL: Record<AtcStatus, string> = {
  proposed: '待建',
  draft: '待补全',
  in_review: '评审中',
  certified: '定义认证',
  active: '在役',
  suspended: '已停用',
  retired: '已退役',
};

/** 岗位四态的中文。**没有 `proposed`、`in_review`、`certified`。** */
export const POSITION_STATUS_LABEL: Record<PositionStatus, string> = {
  draft: '待上线',
  active: '在役',
  suspended: '已停用',
  retired: '已退役',
};

/** 部门三态的中文。**没有 `suspended`**——库级 `department_status_check` 只认这三个。 */
export const DEPARTMENT_STATUS_LABEL: Record<DepartmentStatus, string> = {
  active: '在役',
  merged: '已合并',
  retired: '已退役',
};

/* ═══════ 系统对象（ADR-0020 四） ═══════ */

/**
 * 系统对象的类别。**取值与库级 CHECK 逐字对齐**——约束原文（`pg_get_constraintdef`）：
 *
 * ```
 * department_system_kind_check
 *   CHECK (system_kind = ANY (ARRAY['enterprise_operation','enterprise_construction']))
 * position_system_kind_check
 *   CHECK (system_kind = ANY (ARRAY['department_shared','enterprise_operation','enterprise_construction']))
 * ```
 *
 * **部门只有两个取值，岗位有三个**——岗位多出的 `department_shared` 就是「共享岗／部门岗」，
 * 部门那一层没有对应物。两处若共用一个枚举，部门就能选到一个库会拒的值：
 * 界面允许了、接口拒绝了，那是本仓库反复记的那种漂移。
 */
/**
 * 部门的运营 / 建设类别（C-165 A2）。
 *
 * 与 `atc.cls` 同形，理由也同：**必填、无默认**，建部门时必须明确是哪一类。
 * 岗位的类别**随所属部门**，没有独立字段——多一列就多一个能与父级不一致的地方。
 */
export const departmentClassSchema = z.enum(['operational', 'construction']);

export type DepartmentClass = z.infer<typeof departmentClassSchema>;

/** 界面上这两个值怎么念。**只有这一处。** */
export const DEPARTMENT_CLASS_LABEL: Record<DepartmentClass, string> = {
  operational: '运营类',
  construction: '建设类（交付）',
};

export const departmentSystemKindSchema = z.enum([
  'enterprise_operation',
  'enterprise_construction',
]);
export type DepartmentSystemKind = z.infer<typeof departmentSystemKindSchema>;

export const positionSystemKindSchema = z.enum([
  'department_shared',
  'enterprise_operation',
  'enterprise_construction',
]);
export type PositionSystemKind = z.infer<typeof positionSystemKindSchema>;

/** 界面文案。**部门标「系统」、岗位标「共享」**——照原型 v0.22（859／867 行），不自创。 */
export const SYSTEM_KIND_LABEL: Record<string, string> = {
  enterprise_operation: '企业运营',
  enterprise_construction: '企业建设',
  department_shared: '部门共享',
};

/**
 * `is_system` 与 `system_kind` 的一致性。**库级约束的第二次表达**——原文：
 *
 * ```
 * CHECK ((is_system AND system_kind IS NOT NULL) OR ((NOT is_system) AND system_kind IS NULL))
 * ```
 *
 * 在契约层再拦一道，是为了让人**在表单上就看到为什么**，而不是提交后收到一句约束名。
 * 两处规则一致由测试盯（改库约束时这里同批改）。
 */
function systemKindConsistent(v: {
  isSystem?: boolean | undefined;
  systemKind?: string | null | undefined;
}): boolean {
  if (v.isSystem === undefined && v.systemKind === undefined) return true;
  const isSystem = v.isSystem ?? false;
  const kind = v.systemKind ?? null;
  return isSystem ? kind !== null : kind === null;
}

/*
 * 这是 zod 的 refine 提示，会**原样出现在界面上的错误提示里**——
 * 故不写判据编号（ADR-0020 四；库级 `*_system_kind_consistent`）。
 * 编号留在这条注释里，界面上只留人看得懂的那半句。
 */
const SYSTEM_KIND_MESSAGE = '系统对象必须有类别，非系统对象不能有类别';

/* ═══════ 部门 ═══════ */

/**
 * 部门画像四项（P-23、C-088 二／四）。
 *
 * **人数这一项要当心**：`headcountSurveyed` 是**问卷填报的数，不是编制分母**。
 * 分母的唯一来源是 `position_headcount`（MF-02），部门侧只做汇总不做存储。
 * 两者的差额是核对项，不是错误——沃德夫问卷「① 表写 78、十部门相加 77」就是一例。
 */
export const departmentProfileSchema = z.object({
  /** 部门负责人。填名字即可——人员本体尚未接入界面（P-4） */
  headRef: z.string().trim().max(60).default(''),
  /** 主要职能简述 */
  charter: z.string().trim().max(4000).default(''),
  /** **问卷填报的人数，不是分母。** 不填就是不填，不要填 0 充数 */
  headcountSurveyed: z.number().int().min(0).nullable().default(null),
  /** 来源标注，如「问卷 V2.0 / 2026-06-25 / 待客户确认」 */
  sourceNote: z.string().trim().max(200).default(''),
});

export const createDepartmentSchema = z
  .object({
    /**
     * **编码不收**（ADR-0052 裁定一）：两位数字、系统生成、创建即锁。
     * 由 `allocate_department_code()` 在库层按 cls / system_kind 发放——
     * 收外部传入的 code 就是一条绕过号段与固定号的写入旁路。
     */
    name: orgNameSchema,
    /**
     * 系统部门（ADR-0020 四）：**系统创建，不可改名、不可改归属、不可删除**。
     * 原型里是「企业运营部」「企业建设部」两个。
     */
    isSystem: z.boolean().default(false),
    systemKind: departmentSystemKindSchema.nullable().default(null),
    /** 运营类 / 建设类。**必填无默认**——库里也是 NOT NULL 无 DEFAULT，两处同形。 */
    cls: departmentClassSchema,
  })
  .extend(departmentProfileSchema.shape)
  .refine(systemKindConsistent, SYSTEM_KIND_MESSAGE);

export type CreateDepartmentInput = z.input<typeof createDepartmentSchema>;

/** 解析之后的形状。服务端用它。 */
export type CreateDepartmentData = z.infer<typeof createDepartmentSchema>;

export const updateDepartmentSchema = z
  .object({
    /** 类别录错了要能改。`optional` 是「这次没改它」，不是「可以为空」。 */
    cls: departmentClassSchema.optional(),
    name: orgNameSchema.optional(),
    status: departmentStatusSchema.optional(),
    headRef: z.string().trim().max(60).optional(),
    charter: z.string().trim().max(4000).optional(),
    headcountSurveyed: z.number().int().min(0).nullable().optional(),
    sourceNote: z.string().trim().max(200).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有要改的内容');

/**
 * **`isSystem` / `systemKind` 不在 update 里**——系统对象的这两项建好就不能改。
 *
 * 库级触发器 `system_object_protected` 已经拦了改名与改归属；
 * 契约这里连字段都不给，是为了让「改不了」在**调用方看得见的地方**成立，
 * 而不是提交之后被触发器拒。
 */

export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>;

export const departmentSchema = z.object({
  /** 运营类 / 建设类。台账的「类别」列读它。 */
  cls: departmentClassSchema,
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: departmentStatusSchema,
  /** 这个部门下有多少岗位。 */
  positionCount: z.number(),
  effectiveFrom: z.string(),

  headRef: z.string(),
  charter: z.string(),
  sourceNote: z.string(),

  /** 系统部门：系统创建，不可改名、不可改归属、不可删除（ADR-0020 四）。 */
  isSystem: z.boolean(),
  systemKind: departmentSystemKindSchema.nullable(),
  /** **问卷填报的人数。** 没填是 null——不要用 0 代表「没填」 */
  headcountSurveyed: z.number().nullable(),
  /**
   * **编制人数：这个部门在役岗位的当前编制之和。**
   *
   * 它才是替代率分母那一侧的数（MF-02）。与 `headcountSurveyed` 对不上时，
   * **两个数都不改**——差额是核对项。
   */
  headcountPlanned: z.number(),
});

export type Department = z.infer<typeof departmentSchema>;

/* ═══════ 岗位 ═══════ */

/**
 * C-05 四项里的三项 JSON／文本字段。
 *
 * 空值是合法的**中间态**，不是错误——建岗时可以一个都不填。
 * 它们只在 `active` 那一刻被库检查。
 */
export const createPositionSchema = z
  .object({
    departmentId: z.string().min(1, '请选择所属部门'),
    /**
     * 编码。**留空由系统按部门生成**（C-207：岗位编码仅用于显示，ADR-0052 裁定六——
     * 今晚录 60 个岗位不该被要求先编 60 个码）。显式给的值仍按原规则校验。
     */
    code: orgCodeSchema.or(z.literal('')).default(''),
    name: orgNameSchema,
    /** 岗位使命。库里 NOT NULL，但允许空串——先建后补。 */
    mission: z.string().trim().max(500).default(''),
    /** C-05 · 责任。`active` 时须非空白。 */
    responsibility: z.string().trim().max(2000).default(''),
    /** C-05 · 权限边界。`active` 时须不为 `{}`。 */
    authorityScope: z.record(z.string(), z.unknown()).default({}),
    /** C-05 · 胜任标准。`active` 时须不为 `{}`。 */
    competencyCriteria: z.record(z.string(), z.unknown()).default({}),

    /**
     * 卡位总数，**建岗时就能填**（C-164 A3）。
     *
     * 此前只有 update 收这一项，于是「建岗 → 再改一次」是唯一路径。
     * 录一个部门十来个岗位时，那等于把每个岗位都点两遍。
     *
     * `null` = 还没规划，与 `0`（规划过、结论是不需要卡）不同。
     * 默认 `null` 而不是 `0`——默认成 0 会让没填过的岗位看起来「规划为零」。
     */
    plannedCardCount: z.number().int().min(0).max(9999).nullable().default(null),

    /**
     * 系统岗位（ADR-0020 三、四）。**界面上标「共享」**——照原型 v0.22（867 行）。
     *
     * 岗位这一层比部门多一个取值 `department_shared`，即原型里每个部门下那个
     * 「XX部门岗」。**共享范围由系统岗位承载，不靠挂载层级表达**（ADR-0020 三）
     * ——这正是 I-1 只允许三层却仍能表达「全公司通用」的办法。
     */
    isSystem: z.boolean().default(false),
    systemKind: positionSystemKindSchema.nullable().default(null),
  })
  .refine(systemKindConsistent, SYSTEM_KIND_MESSAGE);

export type CreatePositionInput = z.input<typeof createPositionSchema>;

/** 解析之后的形状。服务端用它，客户端用上面那个。 */
export type CreatePositionData = z.infer<typeof createPositionSchema>;

export const updatePositionSchema = z
  .object({
    name: orgNameSchema.optional(),
    mission: z.string().trim().max(500).optional(),
    responsibility: z.string().trim().max(2000).optional(),
    authorityScope: z.record(z.string(), z.unknown()).optional(),
    competencyCriteria: z.record(z.string(), z.unknown()).optional(),
    /**
     * 卡位总数。`null` 是**合法的写入值**——意思是「撤回这个调研结论」，
     * 与「没传这个字段」（不动）不同。故用 `.nullable().optional()` 两层。
     */
    plannedCardCount: z.number().int().min(0).max(9999).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有要改的内容');

export type UpdatePositionInput = z.infer<typeof updatePositionSchema>;

export const positionSchema = z.object({
  id: z.string(),
  departmentId: z.string(),
  code: z.string(),
  name: z.string(),
  mission: z.string(),
  responsibility: z.string(),
  authorityScope: z.record(z.string(), z.unknown()),
  competencyCriteria: z.record(z.string(), z.unknown()),
  /** 岗位空间。建岗时同事务建出来，所以正常情况下非空。 */
  spaceId: z.string().nullable(),

  /** 系统岗位：界面标「共享」。`department_shared` 即原型里的「XX部门岗」。 */
  isSystem: z.boolean(),
  systemKind: positionSystemKindSchema.nullable(),
  status: positionStatusSchema,
  /** 挂在这个岗位下的 ATC 数。 */
  atcCount: z.number(),
  /**
   * 当前编制人数。**null = 还没录过**，不是 0。
   *
   * 这是 AI 替代率分母那一侧的数（MF-02：分母 = 编制人数 × 标准工作日小时数）。
   * 把「没录」显示成 0，分母就会静静地按 0 算，而 0 分母的替代率无从判断真假。
   */
  headcount: z.number().nullable(),

  /* ═══════ 卡位（C-161 A2-1） ═══════
   *
   * 卡位总数 = 这个岗位**规划应有**的 ATC 张数。**人填的调研结论。**
   *
   * **不得由人数推算**（C-161 口径一）：ATC 数是「有多少种活」，
   * 人数是「有多少人干」。财务处理发票是一张卡、一天二十次执行——
   * 一个人可以守一张卡，二十个人也可以共用一张卡，两者没有换算关系。
   *
   * `null` = **还没调研**，不是 0。0 是「调研过，结论是这个岗位不需要卡」。
   * 空位数由库算（`v_position_card_slots`），前端不减法——
   * 前端算一遍、库算一遍，两处一旦不同，屏幕上会出现库里不存在的空位数。
   */
  plannedCardCount: z.number().int().nullable(),
  /** 已立卡数（不含 retired）。库算。 */
  filledCardCount: z.number().int(),
  /** 空位数 = 卡位总数 − 已立卡数，**下限 0**。卡位总数为 null 时同为 null。库算。 */
  vacantCardCount: z.number().int().nullable(),
  /**
   * 已超数 = 已立卡数 − 卡位总数，**只在为正时非零**。库算（T-148）。
   *
   * 与 `vacantCardCount` **互斥**：一个为正时另一个必为 0，两者同时为正不可能。
   * 现实超出规划是常事，而且它本身是信息——这个岗位的活比当初规划的多，
   * 所以它是**合法状态**，界面上用中性徽标而不是告警色。
   *
   * `null` = 还没规划过（没有分母，谈不上超），与 `0`（规划过、没超）不同。
   */
  overCardCount: z.number().int().nullable(),

  /**
   * 在岗人数：**统计值**，由人员的任职区间数出来（`v_position_staffed_count`）。
   *
   * 与 `headcount`（编制人数）**是两个数**，不得互相顶替：
   * 编制是「这个岗位规划几个人」，人填的调研结论；在岗是「此刻实际几个人」，
   * 从 `person_assignment` 数出来的。两者对不上是核对项，不是错误。
   *
   * **没有人工填报入口**，所以这里只出现在读侧，不出现在任何 create／update 契约里。
   */
  staffedCount: z.number().int(),

  effectiveFrom: z.string(),
});

export type Position = z.infer<typeof positionSchema>;

/**
 * C-05 四项的填写完备性。**由服务端算，前端只显示**——
 * 判断规则与库级约束 `position_active_requires_triplet` 是同一套，
 * 两处各写一遍必然漂移。
 */
export const positionReadinessSchema = z.object({
  responsibility: z.boolean(),
  authorityScope: z.boolean(),
  competencyCriteria: z.boolean(),
  space: z.boolean(),
  /** 四项齐了才能 `active`。 */
  canActivate: z.boolean(),
});

export type PositionReadiness = z.infer<typeof positionReadinessSchema>;

export const positionWithReadinessSchema = positionSchema.extend({
  readiness: positionReadinessSchema,
});

export type PositionWithReadiness = z.infer<typeof positionWithReadinessSchema>;

export const departmentListSchema = z.object({ rows: z.array(departmentSchema) });
export const positionListSchema = z.object({ rows: z.array(positionWithReadinessSchema) });

/* ═══════ ATC（三层的第三层） ═══════ */

/**
 * 建卡。**ATC 只挂岗位**——没有 parentId 这个自由参数，只有 positionId
 * （I-1、ADR-0020 一；库级 `atc.position_id` NOT NULL FK）。
 *
 * 编码不收：它由服务端按规则生成。ATC 编码要印到设备标签与二维码上
 * （ADR-0021 `:25`／`:53`），让人手填会填错、还会重号。
 */
export const atcClassSchema = z.enum(['operational', 'construction']);

export type AtcClass = z.infer<typeof atcClassSchema>;

/** 界面上这两个值怎么念。**只有这一处**——两处各写一遍必然漂移。 */
export const ATC_CLASS_LABEL: Record<AtcClass, string> = {
  operational: '运营 ATC',
  construction: '建设 ATC',
};

/**
 * 同一个轴的**短形**：表格的类别列徽标与台账工具条的筛选档用它。
 *
 * 为什么另立一份而不是复用上面那个：长形在句子里读得通（「全部 / 运营 ATC / 建设 ATC」
 * 是侧栏那一列的筛选器），短形在一格宽的徽标里读得通。原型两处本来就是两种写法。
 * **两种写法各自只有一处定义**——真正会漂移的是同一种写法被抄了两遍。
 *
 * 与 `ownerScope`（client / provider）**是两个轴**，不得互相顶替。
 */
export const ATC_CLASS_SHORT_LABEL: Record<AtcClass, string> = {
  operational: '运营',
  construction: '建设',
};

export const createAtcSchema = z.object({
  positionId: z.string().min(1, '请选择所属岗位'),
  name: orgNameSchema,
  /** 客户 ATC 还是我方 ATC（`atc.owner_scope`，ADR-0007）。 */
  ownerScope: z.enum(['client', 'provider']).default('client'),
  /**
   * 运营卡还是建设卡。**必填，没有默认值。**
   *
   * 与 `ownerScope` 是**两个轴**：这一个说「干的是哪一类活」，
   * 那一个说「归谁所有」。建设卡的执行方多为我方、可见方是客户——
   * 今天两轴在数据上重合，明天客户自己建一张建设卡就分道扬镳。
   *
   * 不给默认值是刻意的：**有默认值的话，没想清楚的卡会全部掉进默认档，
   * 这个轴就失去区分力**。库级同样是 NOT NULL 且无 DEFAULT，两处判据同形。
   */
  cls: atcClassSchema,
  /** 待建态必填的一句话（`atc_proposed_requires_summary`）。留空则落 `draft`。 */
  proposalSummary: z.string().trim().max(500).default(''),
});

/**
 * **用 `z.input` 而不是 `z.infer`。**
 *
 * 带 `.default()` 的字段在 `z.infer`（输出型）里是必填的——那是解析**之后**的形状。
 * 调用方给的是解析**之前**的形状，默认值正是它可以不给的那些。
 * 用输出型当入参类型，等于把「有默认值」写成了「必须传」，默认值就白设了。
 */
export type CreateAtcInput = z.input<typeof createAtcSchema>;

/** 解析之后的形状——服务端拿到的是这个，默认值已经填上了。 */
export type CreateAtcData = z.infer<typeof createAtcSchema>;

/* ═══════ 基线耗时五项（C-161 A2-2） ═══════
 *
 * **两个耗时，永不合一**：
 *
 * | 数 | 落在哪 | 为什么 |
 * |---|---|---|
 * | 基线单次耗时（改造前，人做一次多久） | `atc` 的列 | 建卡那刻的快照，**随卡终身** |
 * | 实际均耗（改造后，实际跑一次多久） | 视图，由执行记录派生 | 天天在变 |
 *
 * 基线落卡不落版本：版本迭代改的是四段定义，而基线是改造成效的**分母**。
 * 分母跟着版本走，「从 40 分钟压到 6 分钟」这句话的前一半就会被悄悄改掉。
 *
 * 一小时准则是**建设排序的辅助下限，不是 ATC 成立门槛**（C-161 口径二）：
 * 成立的判据是四段写得出来。故这里**没有任何时长阈值**——
 * 阈值可调，写进契约就改不动了。
 */
export const baselineFrequencyUnitSchema = z.enum(['day', 'week', 'month']);

export const baselineFieldsSchema = z.object({
  /** 改造前，人类员工完成一次所需分钟数。 */
  baselineDurationMin: z.number().int().positive().max(100000).nullable(),
  baselineFrequency: z.number().positive().max(100000).nullable(),
  baselineFrequencyUnit: baselineFrequencyUnitSchema.nullable(),
  /** true = 调研估计值。事件触发的卡只能估——估值与实测值长得一样，不标就分不出来。 */
  baselineFrequencyEstimated: z.boolean(),
  /** 改基线必须写为什么改：悄悄改分母等于改结论。 */
  baselineRevisionReason: z.string().nullable(),
});

export const atcRowSchema = z
  .object({
    id: z.string(),
    positionId: z.string(),
    code: z.string(),
    name: z.string(),
    ownerScope: z.enum(['client', 'provider']),
    status: z.string(),
    effectiveFrom: z.string(),
    /** 运营卡 / 建设卡。与 `ownerScope` 并列，两个轴各自出现一次。 */
    cls: atcClassSchema,
    /** 基线日总耗时 = 单次 × 归一日均频次。**库算**（视图），不在前端乘。 */
    baselineDailyMinutes: z.number().nullable(),

    /** 成熟度 L0–L5（`atc.maturity_level`）。台账「成熟度」列读它。 */
    maturityLevel: z.enum(['L0', 'L1', 'L2', 'L3', 'L4', 'L5']),
    /**
     * TEST 写了没有。台账那一列「有则绿勾、无则红缺」读它。
     *
     * **判据在库里，不在这里**：`test_spec <> '{}'::jsonb`，与库级约束
     * `atc_test_required_for_certified`（C-06）逐字同源。前端拿到的是结论，不是规则
     * ——前端另判一遍（比如 `Object.keys(testSpec).length > 0`）会在
     * `{"steps": []}` 这类取值上与库给出不同答案，且两边都不报错。
     *
     * 故 `test_spec` 本身**不出契约**：给了原文就会有人在前端再判一次。
     */
    hasTest: z.boolean(),
  })
  .extend(baselineFieldsSchema.shape);

export type AtcRow = z.infer<typeof atcRowSchema>;
export const atcListSchema = z.object({ rows: z.array(atcRowSchema) });

/** 改 ATC。改名与归档／恢复走这里；四段内容走升版（`atc_add_version`），不在这里。 */
export const updateAtcSchema = z
  .object({
    name: orgNameSchema.optional(),

    /* 基线五项可改。频次的数与单位**同生共死**（见下面的 refine）：
       只有数没有单位的「3」无法归一，落进视图会变成一个默认按天算的错值。 */
    baselineDurationMin: z.number().int().positive().max(100000).nullable().optional(),
    baselineFrequency: z.number().positive().max(100000).nullable().optional(),
    baselineFrequencyUnit: baselineFrequencyUnitSchema.nullable().optional(),
    baselineFrequencyEstimated: z.boolean().optional(),
    baselineRevisionReason: z.string().trim().max(1000).nullable().optional(),
    /** 运营 / 建设录错了要能改。`optional` 是「这次没改它」，**不是「可以为空」**。 */
    cls: atcClassSchema.optional(),
    /**
     * 成熟度（卡页编辑态五项之一，C-190 批二）。`atc.maturity_level`，本体档——
     * 「建设到哪个台阶」的评估结论，改了不换卡。
     */
    maturityLevel: z.enum(['L0', 'L1', 'L2', 'L3', 'L4', 'L5']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有要改的内容')
  .refine((v) => {
    // 一次请求里若两者都出现，则要么都有值、要么都为 null。
    // 库级 CHECK 保的是同一条——两处各写一遍必然漂移，故这里的判据与它同形。
    if (!('baselineFrequency' in v) || !('baselineFrequencyUnit' in v)) return true;
    return (v.baselineFrequency === null) === (v.baselineFrequencyUnit === null);
  }, '频次的数与单位必须同时有值或同时为空');

export type UpdateAtcInput = z.infer<typeof updateAtcSchema>;

/* ═══════ 建设成熟度（C-190 批一 A2/A3，派生视图，不落存储列） ═══════
 *
 * 分母 max(卡位总数, 已建卡数)——超额封顶。卡位 null = **未调研**（比率 null，不是 0%）。
 * 卡上显示的比率**按乙生效**（(active + 0.5×certified) / 分母，certified 记半档——
 * C-191 裁定）；甲（纯 active / 分母）保留在三视图里作对照，不删。
 */
export const buildMaturityRowSchema = z.object({
  builtCount: z.number(),
  /** 建设成熟度 0–1。null = 未调研（或无需卡位），**界面显「未调研」，不显 0%**。 */
  maturity: z.number().nullable(),
});

export const buildMaturitySchema = z.object({
  positions: z.array(buildMaturityRowSchema.extend({ positionId: z.string() })),
  departments: z.array(buildMaturityRowSchema.extend({ departmentId: z.string() })),
  enterprise: buildMaturityRowSchema.nullable(),
});
export type BuildMaturity = z.infer<typeof buildMaturitySchema>;

/* ═══════ 状态迁移：三张表共用一个形状（C-165 A3） ═══════
 *
 * **状态不在 `PATCH` 里改。** 三张资产表的状态各有约束（六状态链、
 * C-06 前置、系统对象不可停用、仅追加的历史），全部收在
 * `changeAtcStatus` / `changeDepartmentStatus` / `changePositionStatus` 三个入口里。
 *
 * 之前 `updateXxxSchema` 收 `status`——那是第二条路，而第二条路一定是没校验的那条。
 * **删字段而不是留着忽略**：留着的话客户端传了会被静默丢弃，
 * 界面上看起来改成功了、实际没生效。
 */
export const changeStatusSchema = z.object({
  status: z.string().min(1),
  /**
   * 为什么改。
   *
   * 停用与归档**必填**——一张卡为什么停了，三个月后没人记得，
   * 而那时正是要问的时候。服务端按目标状态判它是否必填。
   */
  reason: z.string().trim().max(500).default(''),
});

export type ChangeStatusInput = z.input<typeof changeStatusSchema>;

/* ═══════ 岗位编制（分母的唯一来源） ═══════ */

/**
 * 设定某个岗位当前的编制人数。
 *
 * **这是 AI 替代率分母的唯一入口**（MF-02：分母 = 编制工时 = 编制人数 × 标准工作日小时数）。
 * 没有这个数，替代率与日总工时两格出不来。
 *
 * `position_headcount` 是**带区间的仅追加表**：改编制不是 UPDATE，
 * 是把旧区间收口、再追加一条新区间。区间不重叠由库级 EXCLUDE 约束保证——
 * 重叠即同一时刻有两个编制数，当天的分母取到哪一个是不确定的。
 */
export const setHeadcountSchema = z.object({
  headcount: z.number().int().min(0),
});

export type SetHeadcountInput = z.infer<typeof setHeadcountSchema>;
